import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState, type PropsWithChildren } from 'react';
import styled from 'styled-components';

import type { Currency } from '@sorare/core/src/__generated__/globalTypes';
import {
  Rarity,
  SeasonEligibility,
} from '@sorare/core/src/__generated__/globalTypes';
import { DisplayIfVisible } from '@sorare/core/src/atoms/layout/DisplayIfVisible';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import type { StatsRarity } from '@sorare/core/src/lib/playerSearchView';

import { SelectCurrency } from '#src/atoms/SelectCurrency/index.tsx';
import { SelectRarity } from '#src/atoms/SelectRarity/index.tsx';
import { SelectSeasonality } from '#src/atoms/SelectSeasonality/index.tsx';
import { TimeframeSelector } from '#src/atoms/TimeframeSelector/index.tsx';
import { PriceGraph } from '#src/components/price/PriceGraph/index.tsx';
import type { SeasonEligibilityOptions } from '#src/lib/seasonalities.ts';
import { seasonEligibilityFromOptions } from '#src/lib/seasonalities.ts';
import type { Timeframe } from '#src/lib/timeframe.ts';
import {
  TIME_FRAME_FROM_TO,
  getTimeframeFromRarity,
} from '#src/lib/timeframe.ts';

import type {
  LazyPriceGraphQuery,
  LazyPriceGraphQueryVariables,
} from './__generated__/index.graphql.ts';

const Filters = styled(Horizontal)`
  overflow: auto;
  justify-content: flex-end;
`;

const LAZY_PLAYER_PRICE_GRAPH_QUERY = gql`
  query LazyPriceGraphQuery(
    $playerSlug: String!
    $rarity: Rarity!
    $seasonEligibility: SeasonEligibility
    $from: ISO8601DateTime
    $to: ISO8601DateTime
  ) {
    tokens {
      tokenPrices(
        first: 20
        playerSlug: $playerSlug
        rarity: $rarity
        seasonEligibility: $seasonEligibility
        from: $from
        to: $to
        includePrivateSales: true
      ) {
        id
        ...PriceGraph_tokenPrice
      }
    }
  }
  ${PriceGraph.fragments.tokenPrice}
` as TypedDocumentNode<LazyPriceGraphQuery, LazyPriceGraphQueryVariables>;

const filterableRarities = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
];

type Props = {
  small?: boolean;
  playerSlug: string;
  currency?: Currency;
  filters?: {
    seasonEligibility?: SeasonEligibilityOptions;
    withSeasonEligibility?: boolean;
    rarity?: StatsRarity;
    timeframe?: Timeframe;
  };
  withLegend?: boolean;
};

const LazyPriceGraphComponent = ({
  small,
  playerSlug,
  currency: currencyProps,
  filters,
  withLegend,
}: Props) => {
  const { currency: userCurrency } = useCurrentUserContext();
  const currency = currencyProps || userCurrency;
  const [currencyState, setCurrencyState] = useState<Currency>(currency);

  const [rarityState, setRarityState] = useState<StatsRarity>(Rarity.limited);
  const rarity: StatsRarity = filters?.rarity || rarityState;

  const [seasonEligibilityState, setSeasonEligibilityState] =
    useState<SeasonEligibilityOptions>(SeasonEligibility.IN_SEASON);
  const seasonEligibility = seasonEligibilityFromOptions(
    (filters?.withSeasonEligibility &&
      (filters?.seasonEligibility || seasonEligibilityState)) ||
      undefined
  );

  const [timeframeState, setTimeframeState] = useState<Timeframe>(
    getTimeframeFromRarity(rarity)
  );
  const timeframe = filters?.timeframe || timeframeState;
  const { from, to } = TIME_FRAME_FROM_TO[timeframe];

  const { data, loading } = useQuery(LAZY_PLAYER_PRICE_GRAPH_QUERY, {
    variables: {
      playerSlug,
      rarity,
      seasonEligibility,
      to,
      from,
    },
  });

  const hasTimeFilter = !filters?.timeframe && !!timeframe;
  const hasSeasonEligibilityFilter =
    !filters?.seasonEligibility &&
    !!filters?.withSeasonEligibility &&
    !!seasonEligibility;
  const hasRarityFilter = !filters?.rarity && !!rarity;
  const hasCurrencyFilter = !currencyProps && !!currencyState;
  const hasFilters =
    hasTimeFilter ||
    hasSeasonEligibilityFilter ||
    hasRarityFilter ||
    hasCurrencyFilter;

  return (
    <>
      {!!hasFilters && (
        <Filters>
          {!!hasTimeFilter && (
            <div>
              <TimeframeSelector
                value={timeframe}
                onChange={setTimeframeState}
                buttonSize="compact"
              />
            </div>
          )}
          {!!hasSeasonEligibilityFilter && (
            <div>
              <SelectSeasonality
                seasonality={seasonEligibility}
                setSeasonality={setSeasonEligibilityState}
                buttonSize="compact"
              />
            </div>
          )}
          {!!hasRarityFilter && (
            <div>
              <SelectRarity
                selectedRarity={rarity}
                onChange={setRarityState}
                rarities={filterableRarities}
                buttonSize="compact"
              />
            </div>
          )}
          {!!hasCurrencyFilter && (
            <div>
              <SelectCurrency
                selectedCurrency={currencyState}
                onChange={value => setCurrencyState(value)}
              />
            </div>
          )}
        </Filters>
      )}
      <PriceGraph
        currency={currencyState}
        loading={loading}
        tokenPrices={data?.tokens.tokenPrices || []}
        withLegend={withLegend}
        small={small}
        from={from}
        to={to}
      />
    </>
  );
};

export const LazyPriceGraph = ({
  children,
  ...props
}: PropsWithChildren<Props>) => {
  return (
    <DisplayIfVisible>
      <LazyPriceGraphComponent {...props} />
    </DisplayIfVisible>
  );
};
